import { MathUtils, Vector3 } from "three";
import { Ticker } from "pixi.js";
import { Projectile } from "./Projectile";

export abstract class ParabolicProjectile extends Projectile {
    private readonly minDist: number = 4;
    private readonly maxDist: number = 50;
    private readonly maxArcHeight: number = 6;
    private target: Vector3;
    private startPosition: Vector3;
    private totalDistance: number;
    private traveledDistance: number;
    private arcHeight: number;

    public override throwToPoint(target: Vector3): void {
        if (this.thrown) {
            throw new Error("You can not throw same projectile twice.");
        }
        this.thrown = true;
        this.startPosition = this.projectileContainer.worldToLocal(this.getWorldPosition(new Vector3()));
        this.target = target.clone();
        this.totalDistance = this.startPosition.distanceTo(this.target);
        this.traveledDistance = 0;

        const clampedDist = MathUtils.clamp(this.totalDistance, this.minDist, this.maxDist);
        const normalized = (clampedDist - this.minDist) / (this.maxDist - this.minDist);
        this.arcHeight = this.maxArcHeight * Math.sin(normalized * (Math.PI / 2));

        this.projectileContainer.add(this);
        this.position.copy(this.startPosition);
        Ticker.shared.add(this.onTick, this);
    }

    public override move(dt: number): void {
        if (!this.target) {
            // fallback to default linear if no target
            super.move(dt);
            return;
        }

        // Calculate horizontal direction vector (from start to target)
        const horizontalVector = this.target.clone();
        horizontalVector.y = 0;
        horizontalVector.sub(this.startPosition.clone().setY(0));
        const horizontalDistance = horizontalVector.length();

        if (horizontalDistance === 0) {
            // Target is directly above or below — just move vertically linearly
            const verticalDir = Math.sign(this.target.y - this.position.y);
            this.position.y += verticalDir * this.speed * dt;
            return;
        }

        const horizontalDir = horizontalVector.normalize();

        // Compute how far we will move this frame along horizontal plane
        const distanceThisFrame = this.speed * dt;
        this.traveledDistance = Math.min(this.traveledDistance + distanceThisFrame, horizontalDistance);

        // Progress from 0 to 1
        const t = this.traveledDistance / horizontalDistance;

        // Compute current horizontal position
        const currentHorizontalPos = this.startPosition.clone().setY(0)
            .add(horizontalDir.clone().multiplyScalar(this.traveledDistance));

        // Parabolic Y position
        const startY = this.startPosition.y;
        const endY = this.target.y;

        // Parabola formula for Y:
        // y(t) = 4h t(1 - t) + lerp(startY, endY, t)
        const y = 4 * this.arcHeight * t * (1 - t) + MathUtils.lerp(startY, endY, t);
        // Set new position
        this.position.set(currentHorizontalPos.x, y, currentHorizontalPos.z);
        // Check if reached target (close enough or traveled full distance)
        if (t >= 1.0 || this.position.distanceTo(this.target) < 0.005) {
            this.velocity.copy(new Vector3(0, -0.1, 0.1));
            this.target = null;
        }
    }
}
